import {
  CSSVariablesResolver,
  DefaultMantineColor,
  MantineTheme,
  MantineThemeOverride,
  rem
} from '@mantine/core';

type PrimaryThemeColor = DefaultMantineColor;

export const FONT_WEIGHT = {
  LIGHT: 300 as const,
  REGULAR: 400 as const,
  MEDIUM: 500 as const,
  SEMIBOLD: 600 as const,
  BOLD: 700 as const,
  XBOLD: 800 as const
};

export const FONT_FAMILY = {
  REGULAR:
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  MONOSPACE:
    '"Google Sans Code", SF Mono, SF Mono Regular, Consolas, "Liberation Mono", Menlo, Courier, monospace',
  HEADINGS:
    '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
};

export const getMantineColor = (
  theme: MantineTheme,
  color: string,
  shade: number,
  fallback: string
): string => {
  const tuple = theme.colors[color];
  if (!tuple) return fallback;
  return tuple[shade] || fallback;
};

export const getPrimaryColorHex = (theme: MantineTheme): string => {
  const shade =
    typeof theme.primaryShade === 'number' ? theme.primaryShade : theme.primaryShade.light;
  return theme.colors[theme.primaryColor]?.[shade] ?? theme.colors.blue[6];
};

export const cssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {
    '--app-font-family-regular': FONT_FAMILY.REGULAR,
    '--app-font-family-monospace': FONT_FAMILY.MONOSPACE,
    '--app-font-family-headings': FONT_FAMILY.HEADINGS,
    '--app-font-weight-regular': String(FONT_WEIGHT.REGULAR),
    '--app-font-weight-medium': String(FONT_WEIGHT.MEDIUM),
    '--app-font-weight-semibold': String(FONT_WEIGHT.SEMIBOLD),
    '--app-font-weight-bold': String(FONT_WEIGHT.BOLD),
    '--app-font-weight-extra-bold': String(FONT_WEIGHT.XBOLD)
  },
  light: {
    '--mantine-color-disabled-color': theme.colors.gray[7],
    '--mantine-color-dimmed': theme.colors.gray[7]
  },
  dark: {}
});

const baseTypography: Pick<
  MantineThemeOverride,
  'fontFamily' | 'headings' | 'black' | 'fontSizes' | 'fontFamilyMonospace'
> = {
  fontSizes: {
    xs: rem(11),
    sm: rem(13),
    md: rem(14),
    lg: rem(16),
    xl: rem(20)
  },
  fontFamilyMonospace: FONT_FAMILY.MONOSPACE,
  fontFamily: FONT_FAMILY.REGULAR,
  black: '#131416', // softer default text color token used by Mantine in light mode
  headings: {
    fontFamily: FONT_FAMILY.HEADINGS,
    sizes: {
      h1: { fontSize: rem(24), lineHeight: rem(32) },
      h2: { fontSize: rem(20), lineHeight: rem(28) },
      h3: { fontSize: rem(18), lineHeight: rem(24) },
      h4: { fontSize: rem(16), lineHeight: rem(22) },
      h5: { fontSize: rem(14), lineHeight: rem(20) },
      h6: { fontSize: rem(11.5), lineHeight: rem(16) }
    }
  }
};

const baseInputSizing: Pick<MantineThemeOverride, 'components'> = {
  components: {
    Text: {
      defaultProps: {
        maw: '650px'
      },
      styles: (_theme: unknown, _props: { c: string }) => ({
        root: {
          // fontWeight: props.c === 'dimmed' ? FONT_WEIGHT.DIMMED : FONT_WEIGHT.REGULAR
        }
      })
    },
    Alert: {
      defaultProps: {
        maw: '700px'
      }
    },
    Checkbox: {
      styles: {
        labelWrapper: {
          fontSize: 'var(--mantine-font-size-md)'
        }
      }
    },
    Switch: {
      styles: {
        label: {
          fontSize: 'var(--mantine-font-size-md)',
          fontFamily: 'var(--mantine-font-family-headings)',
          fontWeight: FONT_WEIGHT.BOLD
        }
      }
    },
    Stepper: {
      styles: {
        stepLabel: {
          fontSize: 'var(--mantine-font-size-md)',
          fontFamily: 'var(--mantine-font-family-headings)'
        },
        stepDescription: {
          fontSize: 'var(--mantine-font-size-md)',
          lineHeight: 1.6,
          letterSpacing: '0.005em'
        }
      }
    },
    SegmentedControl: {
      styles: {
        label: {
          fontSize: 'var(--mantine-font-size-md)',
          fontFamily: 'var(--mantine-font-family-headings)',
          fontWeight: FONT_WEIGHT.BOLD
        }
      }
    },
    Notification: {
      styles: {
        title: {
          fontFamily: 'var(--mantine-font-family-headings)',
          fontSize: 'var(--mantine-font-size-md)',
          fontWeight: FONT_WEIGHT.BOLD
        },
        description: {
          fontSize: 'var(--mantine-font-size-md)',
          lineHeight: 1.6,
          letterSpacing: '0.005em'
        },
        root: {
          maxWidth: 450
        }
      }
    },
    Table: {
      styles: {
        thead: {
          fontFamily: 'var(--mantine-font-family-headings)',
          fontSize: 'var(--mantine-font-size-sm)',
          textTransform: 'uppercase'
        }
      }
    },
    Title: {
      vars: (_theme: unknown, props: { order: number }) => ({
        root: {
          letterSpacing: props.order === 6 ? '0.08em' : undefined,
          textTransform: props.order === 6 ? 'uppercase' : undefined
        }
      }),
      styles: {
        root: {
          letterSpacing: '0.02em'
        }
      }
    },
    Radio: {
      defaultProps: {},
      styles: {
        label: {
          fontFamily: 'var(--mantine-font-family-headings)',
          fontWeight: FONT_WEIGHT.BOLD,
          fontSize: 'var(--mantine-font-size-md)'
        }
      }
    },
    Button: {
      defaultProps: {
        size: 'md'
      },
      vars: (_theme: unknown, props: { size: string; variant: string }) => ({
        root: {
          '--button-height': props.size === 'md' ? rem(38) : undefined,
          boxShadow: props.variant === 'filled' ? '0px 1px 2px rgba(34, 39, 46, 0.15)' : undefined
        }
      }),
      styles: {
        root: {
          paddingInline: rem(16),
          letterSpacing: '0.03em',
          fontFamily: 'var(--mantine-font-family-headings)',
          fontWeight: FONT_WEIGHT.BOLD,
          textTransform: 'capitalize'
        },
        label: {
          fontSize: 'var(--mantine-font-size-md)'
        }
      }
    },
    Modal: {
      styles: {
        body: {
          paddingBottom: 'var(--mantine-spacing-sm)'
        },
        title: {
          fontFamily: 'var(--mantine-font-family-headings)',
          fontWeight: FONT_WEIGHT.BOLD,
          fontSize: '1.125rem'
        }
      }
    },
    Badge: {
      styles: {
        root: {
          fontWeight: FONT_WEIGHT.XBOLD,
          letterSpacing: '0.03em',
          fontFamily: 'var(--mantine-font-family-headings)'
        }
      }
    },
    Paper: {
      styles: {
        root: {
          borderColor: 'var(--mantine-color-gray-2)'
        }
      }
    },
    InputWrapper: {
      styles: {
        label: {
          fontFamily: 'var(--mantine-font-family-headings)',
          fontWeight: FONT_WEIGHT.BOLD
        },
        description: {
          lineHeight: 1.6,
          letterSpacing: '0.005em',
          fontSize: 'var(--mantine-font-size-sm)'
        },
        error: {
          lineHeight: 1.6,
          letterSpacing: '0.005em',
          marginTop: 4
        }
      }
    }
  }
};

const baseSpacing: Pick<MantineThemeOverride, 'spacing'> = {
  spacing: {
    xxs: '0.25rem'
  }
};

const baseTheme: MantineThemeOverride = {
  ...baseTypography,
  ...baseInputSizing,
  ...baseSpacing
};

export const themePrimaryColors: Omit<DefaultMantineColor, 'red' | 'dark' | 'gray'>[] = [
  'blue',
  'green',
  'indigo',
  'pink',
  'teal',
  'violet',
  'orange',
  'yellow',
  'cyan',
  'grape',
  'lime'
];

export const mantineColors: DefaultMantineColor[] = [
  ...themePrimaryColors,
  'red',
  'dark',
  'gray'
] as DefaultMantineColor[];

const themeConfigs: Record<PrimaryThemeColor, MantineThemeOverride> = {
  blue: {
    ...baseTheme,
    primaryColor: 'blue'
  },
  green: {
    ...baseTheme,
    primaryColor: 'green'
  },
  indigo: {
    ...baseTheme,
    primaryColor: 'indigo'
  },
  pink: {
    ...baseTheme,
    primaryColor: 'pink'
  },
  red: {
    ...baseTheme,
    primaryColor: 'red'
  },
  teal: {
    ...baseTheme,
    primaryColor: 'teal'
  },
  violet: {
    ...baseTheme,
    primaryColor: 'violet'
  },
  orange: {
    ...baseTheme,
    primaryColor: 'orange'
  },
  yellow: {
    ...baseTheme,
    primaryColor: 'yellow'
  },
  cyan: {
    ...baseTheme,
    primaryColor: 'cyan'
  },
  grape: {
    ...baseTheme,
    primaryColor: 'grape'
  },
  lime: {
    ...baseTheme,
    primaryColor: 'lime'
  },
  dark: {
    ...baseTheme,
    primaryColor: 'dark'
  },
  gray: {
    ...baseTheme,
    primaryColor: 'gray'
  }
};

export const getThemeConfig = (
  color?: PrimaryThemeColor | null
): MantineThemeOverride | undefined => {
  if (!color || !(color in themeConfigs)) {
    return themeConfigs.blue;
  }
  return themeConfigs[color];
};

export type { PrimaryThemeColor };
